import React, { useEffect, useState, useSyncExternalStore } from "react";
import { ArrowRight, ArrowLeft, EnvelopeSimple } from "@phosphor-icons/react";

import MobileWelcomeModal from "./welcome/MobileWelcomeModal";
import { NAVY } from "./welcome/WelcomeParts";
import { FeatureSlide, SlideWelcome } from "./welcome/WelcomeSlides";
import { WELCOME_FEATURES } from "./welcome/welcomeFeatures";

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** The overview slide, then one slide per feature. */
const TOTAL_SLIDES = 1 + WELCOME_FEATURES.length;

/* ─── Mobile detection (< 640px, matches Tailwind sm breakpoint) ─── */

const mobileQuery = "(max-width: 639px)";

function subscribeMobile(cb: () => void) {
  const mql = window.matchMedia(mobileQuery);
  mql.addEventListener("change", cb);
  return () => mql.removeEventListener("change", cb);
}

function getSnapshotMobile() {
  return window.matchMedia(mobileQuery).matches;
}

function getServerSnapshotMobile() {
  return false;
}

function useIsMobile() {
  return useSyncExternalStore(subscribeMobile, getSnapshotMobile, getServerSnapshotMobile);
}

/** Slide 0 is the overview; slide N previews feature N-1. */
function nextLabel(currentSlide: number): string {
  const nextFeature = WELCOME_FEATURES[currentSlide];
  return nextFeature ? `Next: ${nextFeature.title}` : "Finish";
}

const NAV_BUTTON_CLASS =
  "flex items-center gap-2 px-4 py-2 rounded-[5px] border border-[rgba(35,78,129,0.3)] hover:border-[#234E81] text-sm font-cvcBody hover:bg-gray-50 transition-colors";

function ModalHeader() {
  return (
    <div className="flex shrink-0 items-center justify-between px-8 py-4">
      <a
        href="https://cvccapitalpartners.sharepoint.com/sites/ai-cvc"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1.5 text-sm font-cvcBody hover:underline"
        style={{ color: NAVY }}
      >
        AI @ CVC
        <ArrowRight size={14} />
      </a>
      <span className="text-[18px] font-cvcBody" style={{ color: NAVY }}>
        DealOS
      </span>
      <a
        href="mailto:ai@cvc.com?subject=DealOS%20Feedback"
        className="flex items-center gap-1.5 text-sm font-cvcBody hover:underline"
        style={{ color: NAVY }}
      >
        Submit feedback
        <EnvelopeSimple size={16} />
      </a>
    </div>
  );
}

function PaginationDots({
  currentSlide,
  onSelect,
}: {
  currentSlide: number;
  onSelect: (slide: number) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: TOTAL_SLIDES }).map((_, i) => (
        <button
          type="button"
          key={i}
          onClick={() => onSelect(i)}
          className={`rounded-full transition-colors ${
            i === currentSlide ? "w-3 h-3" : "w-3 h-3 border border-[rgba(35,78,129,0.3)]"
          }`}
          style={{ backgroundColor: i === currentSlide ? NAVY : "transparent" }}
          aria-label={`Go to slide ${i + 1}`}
        />
      ))}
    </div>
  );
}

function ModalFooter({
  currentSlide,
  onBack,
  onNext,
  onSelect,
}: {
  currentSlide: number;
  onBack: () => void;
  onNext: () => void;
  onSelect: (slide: number) => void;
}) {
  return (
    <div className="flex shrink-0 items-center justify-between px-8 py-4">
      <div className="w-[240px]">
        {currentSlide > 0 && (
          <button type="button" onClick={onBack} className={NAV_BUTTON_CLASS} style={{ color: NAVY }}>
            <ArrowLeft size={14} />
            Back
          </button>
        )}
      </div>

      <PaginationDots currentSlide={currentSlide} onSelect={onSelect} />

      <div className="w-[240px] flex justify-end">
        <button
          type="button"
          onClick={onNext}
          className={`${NAV_BUTTON_CLASS} whitespace-nowrap`}
          style={{ color: NAVY }}
        >
          {nextLabel(currentSlide)}
          <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

/**
 * Only the slide scrolls, so the header and footer (Finish) stay on screen
 * when the viewport is shorter than the slide. Keyed by slide so each one
 * opens scrolled to the top.
 */
function SlideBody({ currentSlide }: { currentSlide: number }) {
  const activeFeature = WELCOME_FEATURES[currentSlide - 1];
  return (
    <div
      key={currentSlide}
      style={{ backgroundColor: "#F9F8F3" }}
      className="flex-1 min-h-0 overflow-y-auto"
    >
      <div className="min-h-[525px]">
        {activeFeature ? <FeatureSlide feature={activeFeature} /> : <SlideWelcome />}
      </div>
    </div>
  );
}

export default function WelcomeModal({ isOpen, onClose }: WelcomeModalProps) {
  const isMobile = useIsMobile();
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (!isOpen || isMobile) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setCurrentSlide(0);
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isMobile, onClose]);

  if (isMobile) {
    return <MobileWelcomeModal isOpen={isOpen} onClose={onClose} />;
  }

  if (!isOpen) return null;

  const handleClose = () => {
    setCurrentSlide(0);
    onClose();
  };

  const handleNext = () => {
    if (currentSlide < TOTAL_SLIDES - 1) {
      setCurrentSlide((s) => s + 1);
    } else {
      handleClose();
    }
  };

  return (
    // Modal + Backdrop (single layer so click-outside works reliably)
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(52, 52, 52, 0.80)" }}
      onMouseDown={handleClose}
    >
      <div
        className="bg-white rounded-[10px] w-full max-w-[900px] max-h-full overflow-hidden flex flex-col"
        style={{ boxShadow: "0px 0px 50px 0px rgba(0,0,0,0.25)" }}
        role="dialog"
        aria-modal="true"
        aria-label="Welcome to DealOS"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <ModalHeader />
        <div className="border-t border-[rgba(52,52,52,0.15)]" />

        <SlideBody currentSlide={currentSlide} />

        <div className="border-t border-[rgba(52,52,52,0.15)]" />
        <ModalFooter
          currentSlide={currentSlide}
          onBack={() => setCurrentSlide((s) => s - 1)}
          onNext={handleNext}
          onSelect={setCurrentSlide}
        />
      </div>
    </div>
  );
}
